import React, { useEffect, useState } from 'react';
import Dashboard from '../../pages/Dashboard/Dashboard';
import Layout from '../Layout/Layout';

/**
 * Smooth transition animation from blurred login to clear dashboard
 * Shows the REAL dashboard component with smooth blur-to-clear transition
 */
const LoginTransition = ({ onComplete }) => {
  const [phase, setPhase] = useState('initial'); // initial, clearing, complete
  const [blurAmount, setBlurAmount] = useState(40); // Start with heavy blur
  const [overlayOpacity, setOverlayOpacity] = useState(0.95); // Overlay opacity
  const [dashboardOpacity, setDashboardOpacity] = useState(0.3); // Dashboard opacity

  useEffect(() => {
    // Phase 1: Initial state (blurred dashboard visible)
    const timer1 = setTimeout(() => {
      setPhase('clearing');
    }, 300);

    // Phase 2: Complete transition and navigate
    const timer2 = setTimeout(() => {
      setPhase('complete');
      onComplete();
    }, 2200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [onComplete]);

  // Smooth blur reduction animation using requestAnimationFrame
  useEffect(() => {
    if (phase === 'clearing') {
      const startTime = Date.now();
      const duration = 1900; // 1.9 seconds for smooth transition
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out cubic) for smooth, professional feel
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        
        // Reduce blur smoothly from 40px to 0px
        setBlurAmount(40 * (1 - easeOutCubic));
        
        // Reduce overlay opacity smoothly from 95% to 0%
        setOverlayOpacity(0.95 * (1 - easeOutCubic));
        
        // Increase dashboard opacity smoothly from 30% to 100%
        setDashboardOpacity(0.3 + (0.7 * easeOutCubic));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [phase]);

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden bg-gray-50">
      {/* Real Dashboard with animated blur and opacity */}
      <div 
        className="absolute inset-0 overflow-auto"
        style={{
          filter: `blur(${blurAmount}px)`,
          opacity: dashboardOpacity,
          transition: 'filter 0.05s linear, opacity 0.05s linear', // Smooth updates
        }}
      >
        <Layout>
          <Dashboard />
        </Layout>
      </div>
      
      {/* Overlay that fades out */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 transition-opacity duration-300 ease-out"
        style={{
          opacity: overlayOpacity,
          backdropFilter: `blur(${Math.max(0, blurAmount - 5)}px)`,
          WebkitBackdropFilter: `blur(${Math.max(0, blurAmount - 5)}px)`,
        }}
      >
        {/* Animated Background Shapes (fade out during transition) */}
        <div className={`absolute inset-0 overflow-hidden transition-opacity duration-700 ${
          phase === 'clearing' ? 'opacity-0' : 'opacity-100'
        }`}>
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        {/* Grid Pattern (fade out during transition) */}
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] transition-opacity duration-700 ${
          phase === 'clearing' ? 'opacity-0' : 'opacity-100'
        }`}></div>

        {/* Success Indicator (appears during transition) */}
        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
          phase === 'clearing' ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-4 shadow-2xl transform transition-transform duration-500">
              <svg 
                className="w-10 h-10 text-white" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={3} 
                  d="M5 13l4 4L19 7" 
                />
              </svg>
            </div>
            <p className="text-white font-semibold text-lg">Welcome Back</p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LoginTransition;

